import { ZapDialog } from '@/components/ZapDialog';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { Zap } from 'lucide-react';
import type { Event } from 'nostr-tools';

interface ZapButtonProps {
  event?: Event;
  target?: Event;
  className?: string;
  showCount?: boolean;
  zapData?: { count: number; totalSats: number; isLoading?: boolean };
}

export function ZapButton({
  event,
  target,
  className = "",
  showCount = true,
  zapData: externalZapData
}: ZapButtonProps) {
  const zapTarget = event || target;
  const { user } = useCurrentUser();
  const { data: author } = useAuthor(zapTarget?.pubkey || '');
  const { webln, activeNWC } = useWallet();

  // Only fetch data if not provided externally
  const { totalSats: fetchedTotalSats, isLoading } = useZaps(
    externalZapData ? [] : zapTarget ?? [],
    webln,
    activeNWC
  );

  // Don't show zap button if no target or user is the author
  if (!zapTarget || (user && user.pubkey === zapTarget.pubkey)) {
    return null;
  }

  // Check if author has lightning address
  const hasLightning = author?.metadata?.lud16 || author?.metadata?.lud06;

  // Use external data if provided, otherwise use fetched data
  const totalSats = externalZapData?.totalSats ?? fetchedTotalSats;
  const showLoading = externalZapData?.isLoading || isLoading;

  return (
    <ZapDialog target={zapTarget}>
      <button
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-600/20 to-orange-600/20 hover:from-yellow-600/30 hover:to-orange-600/30 text-yellow-200 hover:text-yellow-100 transition-all font-medium border border-yellow-500/30 hover:border-yellow-500/50 shadow-lg shadow-yellow-500/10 hover:shadow-yellow-500/20 ${className}`}
        disabled={!hasLightning}
        title={!hasLightning ? 'Creator has no lightning address set' : 'Support this app'}
      >
        <Zap className="h-4 w-4" fill="currentColor" />
        <span className="text-sm font-semibold">
          {!hasLightning ? (
            'No ⚡'
          ) : showLoading ? (
            '...'
          ) : showCount && totalSats > 0 ? (
            `${totalSats.toLocaleString()} sats`
          ) : (
            'Zap'
          )}
        </span>
      </button>
    </ZapDialog>
  );
}